/** @format */

/**
 * 会话分析 — 列表侧栏筛选（布局与 TraceSearch 一致）。
 * 文本条件走 FilterItem + getFieldOptions；数值区间走 FilterRangeItem。
 */

import React, { useState } from 'react'
import { _$ } from '@king-design/react'
import styled from 'styled-components'
import { cloneDeep } from 'lodash'
import { MatchMode } from '../typescript'
import {
  SessionFilterItem,
  isSessionMatchFilter,
  isSessionRangeFilter,
  getSessionMatchFilter,
  getSessionRangeFilter
} from '../typescript'
import { ExpandContainer } from 'src/components'
import { ServiceOverviewSearchProps } from 'src/pages/AIObservability/components/typescript'
import FilterItem from '../../components/FilterItem'
import FilterRangeItem from '../../components/FilterRangeItem'
import { useLocation, useHistory } from 'react-router-dom'
import { replaceSearchParams } from 'src/utils'

/** 文本匹配筛选项配置，key 与后端 Filters.Key 一致 */
const SESSION_MATCH_OPTIONS: { label: string; key: string; isInput?: boolean }[] = [
  { key: 'service_name', label: _$('服务名称') },
  { key: 'session_id', label: _$('会话 ID'), isInput: true }
]

/** 数值区间筛选项配置 */
const SESSION_RANGE_OPTIONS: { label: string; key: string; suffix?: string }[] = [
  { key: 'duration_ms', label: _$('耗时'), suffix: 'ms' },
  { key: 'total_tokens', label: _$('Total Tokens') },
  { key: 'input_tokens', label: _$('输入Token') },
  { key: 'output_tokens', label: _$('输出Token') },
  { key: 'trace_count', label: _$('调用链数') }
]

export interface SessionSearchProps {
  className?: string
  /** 路由带入的服务名，会锁定 service_name 筛选 */
  serviceName?: string
  /** 顶栏时间范围，用于拉取字段候选值 */
  headerSearch: ServiceOverviewSearchProps
  /** 当前 Filters 数组 */
  value: SessionFilterItem[]
  onChange: (next: SessionFilterItem[]) => void
}

/** 解析当前 URL search，供详情请求参数使用 */
function useQuery() {
  return new URLSearchParams(useLocation().search)
}
const SessionSearch: React.FC<SessionSearchProps> = ({
  className,
  serviceName,
  headerSearch,
  value,
  onChange
}) => {
  const history = useHistory()
  const location = useLocation()
  const query = useQuery()
  const serachExpanded = query.get('serachExpanded')
  const [expanded, setExpanded] = useState(serachExpanded === 'true')
  const startTime = headerSearch?.time?.[0]
  const endTime = headerSearch?.time?.[1]
  const matchFilters = value.filter(isSessionMatchFilter)

  /** 更新单条文本匹配，保留区间项不变 */
  const handleMatchChange = (Key: string, Op: MatchMode, Values: string[]) => {
    const rangeFilters = value.filter(isSessionRangeFilter)
    const nextMatch = cloneDeep(matchFilters)
    const idx = nextMatch.findIndex(item => item.Key === Key)
    if (idx !== -1) {
      nextMatch[idx].Op = Op
      nextMatch[idx].Values = Values
    } else {
      nextMatch.push({ Key, Op, Values })
    }
    onChange([...rangeFilters, ...nextMatch])
  }

  /** 更新单条数值区间，保留文本匹配项不变 */
  const handleRangeChange = (
    key: string,
    patch: Partial<{ Start: number | null; End: number | null }>
  ) => {
    const matchItems = value.filter(isSessionMatchFilter)
    const nextRange = cloneDeep(value.filter(isSessionRangeFilter))
    const idx = nextRange.findIndex(item => item.Key === key)
    if (idx !== -1) {
      nextRange[idx] = { ...nextRange[idx], ...patch }
    } else {
      nextRange.push({
        Key: key,
        Start: patch.Start ?? null,
        End: patch.End ?? null
      })
    }
    onChange([...matchItems, ...nextRange])
  }

  return (
    <div className={className}>
      <ExpandContainer
        expandText={_$('展开筛选')}
        collapseText={_$('收起筛选')}
        expanded={expanded}
        onExpandedChange={nextExpanded => {
          replaceSearchParams(history, location, { serachExpanded: nextExpanded })
          setExpanded(nextExpanded)
        }}
      >
        <div className="session-search-form">
          {SESSION_MATCH_OPTIONS.map(({ label, key, isInput }) => {
            const findFilter = getSessionMatchFilter(value, key)
            return (
              <FilterItem
                key={key}
                startTime={startTime}
                endTime={endTime}
                fieldName={key}
                label={_$(label)}
                op={findFilter?.Op}
                values={findFilter?.Values}
                filters={matchFilters}
                disabled={key === 'service_name' && !!serviceName}
                isInput={isInput}
                onChange={handleMatchChange}
              />
            )
          })}
          {SESSION_RANGE_OPTIONS.map(({ label, key, suffix }) => {
            const rangeFilter = getSessionRangeFilter(value, key)
            return (
              <FilterRangeItem
                key={key}
                fieldKey={key}
                label={_$(label)}
                start={rangeFilter?.Start ?? null}
                end={rangeFilter?.End ?? null}
                suffix={suffix}
                onChange={handleRangeChange}
              />
            )
          })}
        </div>
      </ExpandContainer>
    </div>
  )
}

export default styled(SessionSearch)`
  border-top: 1px solid #e0e0e0;
  padding-top: 8px;

  .session-search-form {
    width: 100%;
    box-sizing: border-box;
    padding: 0 0 8px;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;

    @media (max-width: 1280px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
`
